import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { tl, stat } from "@/lib/i18nFormat";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, LoadingBlock, EmptyState, StatusPill } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, MessageSquare } from "lucide-react";

import { useI18n } from "@/context/I18nContext";

const PRIO: Record<string, string> = { normal: "info", important: "warn", urgent: "critical" };

export default function Communications() {
  const {
    t
  } = useI18n();

  const { hasPerm } = useAuth();
  const canSend = hasPerm("comm.send");
  const [sent, setSent] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [tree, setTree] = useState<any>({ regions: [], districts: [], stores: [] });

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("normal");
  const [requiresAck, setRequiresAck] = useState(false);
  const [scopeType, setScopeType] = useState("tenant");
  const [scopeId, setScopeId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/comm/messages/sent").then((r) => setSent(r.data.messages)).catch(() => setSent([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); api.get("/org/tree").then((r) => setTree(r.data)).catch(() => {}); }, [load]);

  const scopeOptions = scopeType === "region" ? tree.regions : scopeType === "district" ? tree.districts : scopeType === "store" ? tree.stores : [];

  const send = async () => {
    if (!title || !body) { toast.error("Title and body are required"); return; }
    if (scopeType !== "tenant" && !scopeId) { toast.error("Select an audience"); return; }
    setBusy(true);
    try {
      const r = await api.post("/comm/messages", {
        title, body, priority, requires_ack: requiresAck,
        audience: { scope_type: scopeType, scope_id: scopeType === "tenant" ? null : scopeId, roles: [] },
      });
      toast.success(tl("Sent to {n} recipient(s)", { n: r.data.recipient_count }));
      setOpen(false); setTitle(""); setBody(""); setScopeId(""); setRequiresAck(false);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    (<div>
      <PageHeader
        title="Communications"
        subtitle="Targeted messages with tracked reads and acknowledgements"
        testId="comm-header"
        actions={canSend && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="compose-button"><Send size={15} className="mr-1.5" />{t("Compose")}</Button></DialogTrigger>
            <DialogContent data-testid="compose-dialog">
              <DialogHeader><DialogTitle>{t("New message")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>{t("Title")}</Label><Input className="mt-1" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="comm-title" /></div>
                <div><Label>{t("Message")}</Label><Textarea className="mt-1" value={body} onChange={(e) => setBody(e.target.value)} data-testid="comm-body" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{t("Priority")}</Label>
                    <Select value={priority} onValueChange={setPriority}>
                      <SelectTrigger className="mt-1" data-testid="comm-priority"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="normal">{t("Normal")}</SelectItem><SelectItem value="important">{t("Important")}</SelectItem><SelectItem value="urgent">{t("Urgent")}</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label>{t("Audience")}</Label>
                    <Select value={scopeType} onValueChange={(v) => { setScopeType(v); setScopeId(""); }}>
                      <SelectTrigger className="mt-1" data-testid="comm-scope-type"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="tenant">{t("Whole company")}</SelectItem><SelectItem value="region">{t("Region")}</SelectItem><SelectItem value="district">{t("District")}</SelectItem><SelectItem value="store">{t("Store")}</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                {scopeType !== "tenant" && (
                  <div><Label>{scopeType}</Label>
                    <Select value={scopeId} onValueChange={setScopeId}>
                      <SelectTrigger className="mt-1" data-testid="comm-scope-id"><SelectValue placeholder={`${tl("Select {scope}", { scope: scopeType })}`} /></SelectTrigger>
                      <SelectContent>{scopeOptions.map((o: any) => <SelectItem key={o.id} value={o.id}>{o.name}{o.store_number ? ` (#${o.store_number})` : ""}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex items-center justify-between rounded-md border border-border p-3">
                  <div><div className="text-sm font-medium">{t("Require acknowledgement")}</div><div className="text-xs text-muted-foreground">{t("Recipients must confirm they've read it")}</div></div>
                  <Switch checked={requiresAck} onCheckedChange={setRequiresAck} data-testid="comm-requires-ack" />
                </div>
              </div>
              <DialogFooter><Button onClick={send} disabled={busy} data-testid="comm-send"><Send size={15} className="mr-1.5" />{busy ? t("Sending…") : t("Send")}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />
      {loading ? <LoadingBlock /> : sent.length === 0 ? (
        <EmptyState icon={<MessageSquare size={28} />} title="No messages sent yet" />
      ) : (
        <Card className="overflow-hidden" data-testid="sent-list">
          {sent.map((m) => (
            <div key={m.id} data-testid={`sent-msg-${m.id}`} className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 last:border-0">
              <div className="min-w-0">
                <div className="font-medium">{m.title}</div>
                <div className="truncate text-xs text-muted-foreground">{m.body}</div>
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span>{m.read_count}/{m.recipient_count}{" "}{t("read")}</span>
                {m.requires_ack && <span className="text-status-good">{m.ack_count}/{m.recipient_count}{" "}{t("acked")}</span>}
                <StatusPill status={PRIO[m.priority] || "info"}>{stat(m.priority)}</StatusPill>
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>)
  );
}
